import { useEffect, useId, useRef, useState } from "react";
import "./PasswordField.css";

/**
 * A password input with a show/hide toggle, for every password field on the
 * site (sign in, create account, reset password).
 *
 * Three decisions worth knowing before changing it:
 *
 * - The label is a real <label htmlFor>, NOT a wrapping <label>. A button
 *   inside a wrapping label is folded into the input's accessible name, so a
 *   screen reader would announce the field as "Password Show password".
 * - The toggle's NAME changes ("Show password" / "Hide password") and it has
 *   no aria-pressed: using both makes a screen reader say "Hide password,
 *   pressed", which reads backwards. A polite live region also says
 *   "Password shown" / "Password hidden", because a name change on a focused
 *   button is not announced by every screen reader.
 * - The field goes back to hidden when its form is submitted, and on unmount
 *   (leaving the page) by construction, since the state lives here. The
 *   input stays the same element throughout, with an `autoComplete` the
 *   caller sets, so password managers and autofill keep working: only its
 *   `type` changes.
 */
const PasswordField = ({
  label, value, onChange, autoComplete, required = true, minLength,
  placeholder, className = "", style, name,
}) => {
  const id = useId();
  const inputRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) return undefined;
    const hide = () => { setVisible(false); setAnnouncement(""); };
    form.addEventListener("submit", hide);
    return () => form.removeEventListener("submit", hide);
  }, []);

  const toggle = () => {
    setAnnouncement(visible ? "Password hidden" : "Password shown");
    setVisible(!visible);
  };

  return (
    <div className={`form-field pw-field ${className}`.trim()} style={style}>
      <label htmlFor={id}>{label}</label>
      <div className="pw-wrap">
        <input
          ref={inputRef}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required={required}
          minLength={minLength}
          placeholder={placeholder}
          className="form-input pw-input"
        />
        <button
          type="button"
          className="pw-toggle"
          aria-label={visible ? "Hide password" : "Show password"}
          aria-controls={id}
          onClick={toggle}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      <span className="pw-sr-only" aria-live="polite">{announcement}</span>
    </div>
  );
};

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"
    fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"
    fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.1" />
    <path d="M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </svg>
);

export default PasswordField;
